import axios from "axios";

const API_URL =
  import.meta.env.VITE_CRM_API_URL || "https://dashboard.lono.co.in/api/crm";

const api = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});
import { creditAPI } from "../../services/CreditManagerPortal/creditApi";

// Add a request interceptor to include the auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token"); // or however you store your token
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// CRM / Leads API
const allowedStatuses = [
  "new-leads",
  "approved",
  "rejected",
  "processing",
  "submitted",
  "in-review",
];

export const crmAPI = {
  getLeadsByStatus: async (status, page = 1, limit = 10) => {
    try {
      if (!allowedStatuses.includes(status)) {
        throw new Error(`Invalid status: ${status}`);
      }
      const response = await api.get(`/leads/status/${status}`, {
        params: { page, limit },
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
  selectFinalBankingPartner: async (loanId, distributionId) => {
      try {
        return await creditAPI.selectFinalBankingPartner(loanId, distributionId);
      } catch (error) {
        throw error;
      }
    },

  getLeadById: async (id) => {
    try {
      // Changed from /leads/:id to /loan/:loanId to match backend route
      const response = await api.get(`/loan/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  getLeadDocuments: async (leadId) => {
    try {
      const response = await api.get(`/leads/${leadId}/documents`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  verifyDocument: async (leadId, documentId, status) => {
    try {
      const response = await api.patch(
        `/leads/${leadId}/documents/${documentId}/verify`,
        {
          status,
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error verifying document:", error);
      throw error;
    }
  },

  updateLoanStatus: async (leadId, leadStatus, message) => {
    try {
      const response = await api.patch(`/loan/${leadId}/update/status`, {
        status: leadStatus,
        message: message, // pass message along with status
      });
      return response.data; // returns updated loan
    } catch (error) {
      console.error(
        "Failed to update loan status:",
        error.response?.data || error.message
      );
      throw error;
    }
  },

  assignLeadToStaff: async (leadId, assignedTo) => {
    try {
      const response = await api.patch(`/loan/${leadId}/assign-lead`, {
        assignedTo,
      });
      return response.data;
    } catch (error) {
      console.error("Error assigning lead to staff:", error);
      throw error;
    }
  },

  // Generate and download loan application PDF
  generateLoanApplicationPDF: async (loanId) => {
    try {
      const response = await api.get(`/loan/${loanId}/generate-pdf`, {
        responseType: "blob",
        headers: {
          "Cache-Control": "no-cache",
          Pragma: "no-cache",
          Expires: "0",
        },
      });

      // Extract filename from headers or use default
      const contentDisposition = response.headers["content-disposition"];
      let filename = `loan_application_${loanId}.pdf`;

      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+?)"?$/);
        if (match && match[1]) {
          filename = match[1].replace(/['"]/g, "");
        }
      }

      // Create download link
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();

      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);

      return { success: true, filename };
    } catch (error) {
      console.error("Error generating PDF:", error);
      if (error.response?.data instanceof Blob) {
        const text = await error.response.data.text();
        throw JSON.parse(text);
      }
      throw error.response?.data || error.message;
    }
  },

  // Download a document with proper error handling
  downloadLeadDocument: async (documentId) => {
    try {
      const response = await api.get(`/documents/${documentId}/download`, {
        responseType: "blob",
        headers: {
          "Cache-Control": "no-cache",
          Pragma: "no-cache",
          Expires: "0",
        },
      });

      // Extract filename from headers or use document ID
      const contentDisposition = response.headers["content-disposition"];
      let filename = documentId;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, "");
        }
      }

      // Create download link
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      link.setAttribute("target", "_blank");
      document.body.appendChild(link);
      link.click();

      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
    } catch (error) {
      console.error("Download error:", error);
      if (error.response?.data instanceof Blob) {
        // Handle blob errors (like when the server returns JSON error as blob)
        const errorText = await error.response.data.text();
        throw JSON.parse(errorText) || "Download failed";
      }
      throw error.response?.data || error.message;
    }
  },

  viewLeadDetailsRoute: (leadId) => `/customers/leads/${leadId}`,

  getAllowedStatuses: () => [...allowedStatuses],

  distributeLoanAndUpdateAmount: async (loanId, distributionData) => {
    try {
      // The backend route is /api/crm/:loanId/distribute-update
      const response = await api.post(
        `/loan/${loanId}/distribute-update`,
        distributionData
      );
      return response.data;
    } catch (error) {
      console.error("Error distributing loan and updating amount:", error);
      throw error.response?.data || error.message;
    }
  },

  updateLeadGeneration: async (loanId, leadGeneration) => {
    try {
      const response = await api.patch(`/loan/${loanId}/lead-generation`, {
        leadGeneration,
      });
      return response.data;
    } catch (error) {
      console.error("Error updating lead generation:", error);
      throw error.response?.data || error.message;
    }
  },
};
